import React, { useState, useMemo, useEffect, useCallback, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as adminService from "@/shared/api/services/adminService";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getMerchantName } from "@/shared/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Calendar } from "@/shared/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Search, Calendar as CalendarIcon, Download, RefreshCw,
  ChevronLeft, ChevronRight, Globe, X,
  SlidersHorizontal, ArrowDownToLine, ArrowUpFromLine, AlertCircle,
} from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";

const C = {
  bg: "#060C18",
  card: "#0E1C35",
  cardHigh: "#122040",
  cardHover: "#162B4D",
  border: "rgba(255,255,255,0.07)",
  borderHover: "rgba(34,211,238,0.35)",
  cyan: "#22D3EE",
  cyanSoft: "rgba(34,211,238,0.08)",
  cyanHover: "rgba(34,211,238,0.15)",
  blue: "#3B82F6",
  emerald: "#10B981",
  rose: "#FB7185",
  t1: "#EFF6FF",
  t2: "#93B4D4",
  t3: "#4D6E90",
  t4: "#2A4060",
};

const DARK_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
.gtx,.gtx *{font-family:'Outfit',system-ui,sans-serif!important;box-sizing:border-box}
@keyframes gtx-spin{to{transform:rotate(360deg)}}
@keyframes gtx-pulse{0%,100%{transform:scale(1);opacity:.5}50%{transform:scale(1.9);opacity:0}}
@keyframes gtx-shimmer{0%{background-position:200% 0}to{background-position:-200% 0}}

.gtx [data-radix-select-trigger],
.gtx button[role="combobox"]{
  background:#0E1C35!important;
  border:1px solid rgba(255,255,255,0.09)!important;
  color:#93B4D4!important;
  border-radius:10px!important;
  height:40px!important;
  font-size:13px!important;
  font-weight:500!important;
  transition:all .2s cubic-bezier(0.4,0,0.2,1)!important;
  position:relative!important;
  overflow:hidden!important;
}
.gtx [data-radix-select-trigger]::before,
.gtx button[role="combobox"]::before{
  content:''!important;
  position:absolute!important;
  inset:0!important;
  background:linear-gradient(90deg,transparent,rgba(34,211,238,0.08),transparent)!important;
  opacity:0!important;
  transition:opacity .2s!important;
}
.gtx [data-radix-select-trigger]:hover,
.gtx button[role="combobox"]:hover{
  background:#162B4D!important;
  border-color:rgba(34,211,238,0.4)!important;
  color:#EFF6FF!important;
  transform:translateY(-1px)!important;
  box-shadow:0 4px 12px rgba(34,211,238,0.15)!important;
}
.gtx [data-radix-select-trigger]:hover::before,
.gtx button[role="combobox"]:hover::before{
  opacity:1!important;
}
.gtx [data-radix-select-trigger]:focus,
.gtx button[role="combobox"]:focus{
  outline:none!important;
  border-color:rgba(34,211,238,0.5)!important;
  box-shadow:0 0 0 3px rgba(34,211,238,0.12)!important;
}
.gtx [data-radix-select-trigger] svg,
.gtx button[role="combobox"] svg{
  color:#4D6E90!important;
  transition:color .2s!important;
}
.gtx [data-radix-select-trigger]:hover svg,
.gtx button[role="combobox"]:hover svg{
  color:#22D3EE!important;
}
.gtx [data-radix-select-trigger] span{color:inherit!important}

[data-radix-select-content]{
  background:linear-gradient(155deg,#122040 0%,#0E1C35 100%)!important;
  border:1px solid rgba(34,211,238,0.25)!important;
  border-radius:14px!important;
  padding:6px!important;
  z-index:9999!important;
  box-shadow:0 24px 60px rgba(0,0,0,0.9),0 0 0 1px rgba(34,211,238,0.08)!important;
  font-family:'Outfit',system-ui,sans-serif!important;
  backdrop-filter:blur(20px)!important;
}
[data-radix-select-content] *{font-family:'Outfit',system-ui,sans-serif!important}
[data-radix-select-item]{
  background:transparent!important;
  color:#93B4D4!important;
  border-radius:8px!important;
  font-size:13px!important;
  font-weight:500!important;
  padding:10px 14px!important;
  cursor:pointer!important;
  outline:none!important;
  transition:all .15s cubic-bezier(0.4,0,0.2,1)!important;
  position:relative!important;
  overflow:hidden!important;
}
[data-radix-select-item]::before{
  content:''!important;
  position:absolute!important;
  inset:0!important;
  background:linear-gradient(90deg,transparent,rgba(34,211,238,0.1),transparent)!important;
  opacity:0!important;
  transition:opacity .2s!important;
}
[data-radix-select-item][data-highlighted],
[data-radix-select-item]:hover{
  background:rgba(34,211,238,0.15)!important;
  color:#22D3EE!important;
  outline:none!important;
  transform:translateX(3px)!important;
}
[data-radix-select-item]:hover::before{
  opacity:1!important;
}
[data-radix-select-item][data-state="checked"]{
  background:rgba(34,211,238,0.22)!important;
  color:#67E8F9!important;
  font-weight:600!important;
}
[data-radix-select-item] span{color:inherit!important}
[data-radix-select-separator]{
  background:rgba(255,255,255,0.07)!important;
  margin:4px 0!important;
}

[data-radix-popover-content]{
  background:linear-gradient(155deg,#0F1F3A 0%,#0A1628 100%)!important;
  border:1px solid rgba(34,211,238,0.22)!important;
  border-radius:16px!important;
  box-shadow:0 24px 60px rgba(0,0,0,0.9)!important;
  font-family:'Outfit',system-ui,sans-serif!important;
  backdrop-filter:blur(20px)!important;
}
[data-radix-popover-content] *{font-family:'Outfit',system-ui,sans-serif!important}
[data-radix-popover-content] button[name="day"],
[data-radix-popover-content] [class*="day_button"]{
  color:#93B4D4!important;
  border-radius:8px!important;
  background:transparent!important;
  transition:all .15s cubic-bezier(0.4,0,0.2,1)!important;
  font-weight:500!important;
}
[data-radix-popover-content] button[name="day"]:hover,
[data-radix-popover-content] [class*="day_button"]:hover{
  background:rgba(34,211,238,0.15)!important;
  color:#22D3EE!important;
  transform:scale(1.05)!important;
}
[data-radix-popover-content] [aria-selected="true"] button,
[data-radix-popover-content] [class*="day_selected"] button{
  background:#22D3EE!important;
  color:#060C18!important;
  font-weight:700!important;
  box-shadow:0 4px 12px rgba(34,211,238,0.3)!important;
}
[data-radix-popover-content] [class*="caption_label"]{
  color:#EFF6FF!important;
  font-weight:600!important;
}
[data-radix-popover-content] [class*="nav"] button{
  background:rgba(255,255,255,0.06)!important;
  border-radius:8px!important;
  color:#EFF6FF!important;
  transition:all .15s!important;
}
[data-radix-popover-content] [class*="nav"] button:hover{
  background:rgba(34,211,238,0.15)!important;
  transform:scale(1.05)!important;
}
[data-radix-popover-content] [class*="head_cell"]{
  color:#4D6E90!important;
  font-size:11px!important;
  font-weight:600!important;
  text-transform:uppercase!important;
  letter-spacing:0.05em!important;
}
[data-radix-popover-content] [class*="day_outside"] button{
  opacity:.3!important;
}

.gtx input[type="text"],
.gtx input:not([type="checkbox"]):not([type="radio"]){
  background:#0E1C35!important;
  border:1px solid rgba(255,255,255,0.09)!important;
  color:#EFF6FF!important;
  border-radius:10px!important;
  height:40px!important;
  font-size:13px!important;
  font-weight:500!important;
  transition:all .2s cubic-bezier(0.4,0,0.2,1)!important;
}
.gtx input::placeholder{
  color:#4D6E90!important;
}
.gtx input:hover{
  border-color:rgba(255,255,255,0.14)!important;
  background:#162B4D!important;
}
.gtx input:focus{
  outline:none!important;
  border-color:rgba(34,211,238,0.5)!important;
  box-shadow:0 0 0 3px rgba(34,211,238,0.12)!important;
  background:#122040!important;
}

::-webkit-scrollbar{width:6px;height:6px}
::-webkit-scrollbar-track{background:rgba(14,28,53,0.3);border-radius:10px}
::-webkit-scrollbar-thumb{background:rgba(34,211,238,0.2);border-radius:10px;transition:background .2s}
::-webkit-scrollbar-thumb:hover{background:rgba(34,211,238,0.35)}
`;

const Panel = memo(({ children, className = "", noPad = false }) => (
  <div className={`rounded-2xl relative overflow-hidden ${noPad ? "" : "p-5"} ${className}`}
    style={{
      background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`,
      border: `1px solid ${C.border}`,
      boxShadow: "0 4px 24px rgba(0,0,0,0.2)"
    }}>
    <div className="absolute top-0 inset-x-0 h-px pointer-events-none"
      style={{ background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.15),transparent)" }} />
    {children}
  </div>
));

const IconBtn = memo(({ onClick, disabled, spinning, icon: Icon, label, variant = "default" }) => {
  const [h, setH] = useState(false);
  const isCyan = variant === "cyan", on = h && !disabled;
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "0 18px",
        height: 40,
        borderRadius: 11,
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "'Outfit',sans-serif",
        border: "1px solid",
        opacity: disabled ? 0.5 : 1,
        transition: "all .2s cubic-bezier(0.4,0,0.2,1)",
        background: on ? (isCyan ? C.cyanHover : "rgba(59,130,246,0.12)") : C.card,
        borderColor: on ? (isCyan ? C.borderHover : "rgba(59,130,246,0.4)") : C.border,
        color: on ? (isCyan ? C.cyan : C.blue) : C.t2,
        boxShadow: on ? (isCyan ? "0 4px 16px rgba(34,211,238,0.2)" : "0 4px 16px rgba(59,130,246,0.15)") : "none",
        transform: on ? "translateY(-1px)" : "none"
      }}>
      <span style={{ display: "inline-flex", animation: spinning ? "gtx-spin .7s linear infinite" : "none" }}>
        <Icon size={15} />
      </span>
      {label}
    </button>
  );
});

const DarkSelect = memo(({ value, onValueChange, placeholder, children }) => (
  <div className="gtx">
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="w-full"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  </div>
));

const DatePickerBtn = memo(({ selected, onSelect, placeholder }) => {
  const [h, setH] = useState(false);
  return (
    <div className="gtx">
      <Popover>
        <PopoverTrigger asChild>
          <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              height: 40,
              padding: "0 14px",
              borderRadius: 10,
              cursor: "pointer",
              background: h ? C.cardHover : C.card,
              fontFamily: "'Outfit',sans-serif",
              fontSize: 13,
              fontWeight: 500,
              border: `1px solid ${h ? C.borderHover : "rgba(255,255,255,0.09)"}`,
              color: selected ? C.t1 : C.t3,
              transition: "all .2s cubic-bezier(0.4,0,0.2,1)",
              boxShadow: h ? "0 4px 12px rgba(34,211,238,0.12)" : "none",
              transform: h ? "translateY(-1px)" : "none"
            }}>
            <CalendarIcon size={15} style={{ color: selected ? C.cyan : C.t3, flexShrink: 0, transition: "color .2s" }} />
            <span>{selected ? format(selected, "MMM d, yyyy") : placeholder}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={selected} onSelect={onSelect} />
        </PopoverContent>
      </Popover>
    </div>
  );
});

const SearchInput = memo(({ value, onChange, placeholder }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div className="gtx" style={{ position: "relative" }}>
      <Search
        size={15}
        style={{
          position: "absolute",
          left: 14,
          top: "50%",
          transform: "translateY(-50%)",
          color: focused ? C.cyan : C.t3,
          pointerEvents: "none",
          zIndex: 1,
          transition: "color .2s"
        }}
      />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ paddingLeft: 38, width: "100%" }}
      />
    </div>
  );
});

const FilterPill = memo(({ label, onRemove }) => {
  const [h, setH] = useState(false);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "5px 12px",
        borderRadius: 9,
        fontSize: 12,
        fontWeight: 600,
        background: C.cyanSoft,
        border: "1px solid rgba(34,211,238,0.25)",
        color: C.cyan,
        transition: "all .2s"
      }}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
    >
      {label}
      <button
        onClick={onRemove}
        style={{
          color: C.cyan,
          background: h ? "rgba(34,211,238,0.15)" : "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          padding: 2,
          borderRadius: 4,
          transition: "all .15s"
        }}
      >
        <X size={12} strokeWidth={2.5} />
      </button>
    </span>
  );
});

const StatChip = memo(({ icon: Icon, label, value, color }) => (
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 18px",
    borderRadius: 13,
    background: `${color}0D`,
    border: `1px solid ${color}22`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    transition: "transform .2s",
  }}
    onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
    onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
  >
    <div style={{
      width: 36,
      height: 36,
      borderRadius: 10,
      background: `${color}15`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <Icon size={16} style={{ color }} />
    </div>
    <div>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: `${color}99`, margin: 0 }}>{label}</p>
      <p style={{ fontSize: 16, fontWeight: 700, color, margin: 0, lineHeight: 1.2 }}>{value}</p>
    </div>
  </div>
));

const PageBtn = memo(({ onClick, disabled, children }) => {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        width: 38,
        height: 38,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        background: h && !disabled ? C.cardHover : C.card,
        border: `1px solid ${h && !disabled ? C.borderHover : C.border}`,
        color: h && !disabled ? C.cyan : C.t2,
        opacity: disabled ? 0.35 : 1,
        transition: "all .2s cubic-bezier(0.4,0,0.2,1)",
        transform: h && !disabled ? "translateY(-1px)" : "none",
        boxShadow: h && !disabled ? "0 4px 12px rgba(34,211,238,0.15)" : "none"
      }}>
      {children}
    </button>
  );
});

const LiveDot = () => (
  <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14 }}>
    <span style={{ position: "absolute", width: "100%", height: "100%", borderRadius: "50%", background: C.emerald, animation: "gtx-pulse 2s ease-in-out infinite" }} />
    <span style={{ position: "relative", width: 7, height: 7, borderRadius: "50%", background: C.emerald, boxShadow: `0 0 8px ${C.emerald}` }} />
  </span>
);

export default function AdminGlobalTransactions() {
  const { t, language } = useLanguage();
  const [filters, setFilters] = useState({ search: "", engine: "all", reseller: "all", merchant: "all", provider: "all", status: "all", type: "all", dateFrom: null, dateTo: null });
  const [selectedTx, setSelectedTx] = useState(null);
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  const pageSize = 25;
  const [merchantIds, setMerchantIds] = useState([]);

  useEffect(() => {
    const id = "gtx-css";
    if (document.getElementById(id)) return;
    const el = document.createElement("style"); el.id = id; el.textContent = DARK_CSS;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { data: engines = [] } = useQuery({ queryKey: ["engines"], queryFn: () => adminService.getAllEngines() });
  const { data: resellers = [] } = useQuery({ queryKey: ["resellers"], queryFn: () => adminService.getAllResellers() });
  const { data: merchants = [] } = useQuery({ queryKey: ["merchants"], queryFn: () => adminService.getAllMerchants() });

  const filtersMapper = React.useMemo(() => ({
    provider: filters.provider !== "all" ? filters.provider : undefined,
    status: filters.status !== "all" ? filters.status : undefined,
    type: filters.type !== "all" ? filters.type.toUpperCase() : undefined,
    startDate: filters.dateFrom ? filters.dateFrom.toISOString() : undefined,
    endDate: filters.dateTo ? filters.dateTo.toISOString() : undefined,
  }), [filters]);

  const { data: combinedQRCodeAndWitData, isLoading: isLoadingQRAndWithdrawals, refetch: refetchWithdrawalsAndQr, error: combinedError } = useQuery({
    queryKey: ["merchant-withdrawals", page, pageSize, filters.search, filtersMapper, merchantIds],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      page + 1,
      pageSize,
      filters.search,
      filtersMapper,
      merchantIds
    ),
  });

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try { await refetchWithdrawalsAndQr(); } catch (e) { console.error(e); }
    finally { setTimeout(() => setIsRefreshing(false), 500); }
  }, [refetchWithdrawalsAndQr]);

  const combinedDataWithdrawalsAndQRCodes = combinedQRCodeAndWitData?.data || [];

  const combinedData = React.useMemo(() => {
    return combinedDataWithdrawalsAndQRCodes.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference,
      type: t.type?.toUpperCase() || "UNKNOWN",
      status: (t.status || "").toLowerCase(),
      amount: t.amount,
      provider_display: t.bank_code || t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      bank_name: t.bank_name,
      bank_code: t.bank_code,
      merchant: t.merchant || (t.merchant_id ? merchants.find(m => m.id === t.merchant_id) : null) || {
        name: t.merchant_name,
        id: t.merchant_id,
        code: t.merchant_code,
      },
      reseller: t.reseller || (t.merchant?.createdByResellerId ? resellers.find(r => r.id === t.merchant.createdByResellerId) : null) || {
        name: t.reseller_name,
        id: t.reseller_id,
        code: t.reseller_code,
      },
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
    })).sort((a, b) => new Date(b.created_date || b.createdAt) - new Date(a.created_date || a.createdAt));
  }, [combinedDataWithdrawalsAndQRCodes, merchants, resellers]);

  const isLoading = isLoadingQRAndWithdrawals;
  const error = combinedError;

  const paginatedTransactions = combinedData;
  const totalPages = combinedQRCodeAndWitData?.totalPages || 0;
  const totalTransactions = combinedQRCodeAndWitData?.total || 0;

  const { data: providerOptions = [] } = useQuery({
    queryKey: ["combined-providers"],
    queryFn: () => combinedTransactionService.getAllProvider(),
  });

  const setFilter = useCallback((key, val) => {
    setFilters(p => ({ ...p, [key]: val }));
    setPage(0);
  }, []);

  const handleEntityFilterChange = useCallback((type, value) => {
    let newMerchantIds = [];
    if (value !== 'all') {
      if (type === 'engine') {
        newMerchantIds = merchants.filter(m => m.createdByEngineId === value).map(m => m.id);
        setFilters((prev) => ({ ...prev, engine: value, reseller: "all", merchant: "all" }));
      } else if (type === 'reseller') {
        newMerchantIds = merchants.filter(m => m.createdByResellerId === value).map(m => m.id);
        setFilters((prev) => ({ ...prev, reseller: value, engine: "all", merchant: "all" }));
      } else if (type === 'merchant') {
        newMerchantIds = [value];
        setFilters((prev) => ({ ...prev, merchant: value, engine: "all", reseller: "all" }));
      }

      if (newMerchantIds.length === 0) {
        newMerchantIds = ["NO_MERCHANT_FOUND"];
      }

      setMerchantIds(newMerchantIds);
      setPage(0);
    } else {
      if (type === 'engine') setFilters(p => ({ ...p, engine: 'all', reseller: 'all', merchant: 'all' }));
      if (type === 'reseller') setFilters(p => ({ ...p, reseller: 'all', merchant: 'all' }));
      if (type === 'merchant') setFilters(p => ({ ...p, merchant: 'all' }));
      setMerchantIds([]);
      setPage(0);
    }
  }, [merchants]);

  const resetFilters = useCallback(() => {
    setFilters({ search: "", engine: "all", reseller: "all", merchant: "all", provider: "all", status: "all", type: "all", dateFrom: null, dateTo: null });
    setMerchantIds([]);
    setPage(0);
  }, []);

  const handleExport = useCallback(() => {
    const h = [t("type"), t("paymentId"), t("reference"), t("merchant"), t("reseller"), t("provider"), t("grossAmount") || "Gross Amount", t("totalFee") || "Total Fee", t("netAmount") || "Net Amount", t("status"), t("date")];
    const rows = combinedData.map(tx => [
      tx.type === "WITHDRAWAL" || tx.type === "withdrawal" || tx.type === "Withdrawal" ? t("withdrawal") : t("deposit"),
      tx.transaction_id || tx.payment_id || tx.id,
      tx.reference || (tx.type === "WITHDRAWAL" || tx.type === "withdrawal" || tx.type === "Withdrawal" ? tx.bank_account || "" : "") || "",
      tx.merchant?.name || tx.merchant?.code || (tx.merchant_id ? (merchants.find(m => m.id === tx.merchant_id)?.name || "") : "") || "",
      tx.reseller?.name || (tx.merchant?.createdByResellerId ? (resellers.find(r => r.id === tx.merchant.createdByResellerId)?.name || "") : "Direct") || "Direct",
      tx.client_bank_name || tx.bank_name || tx.bank_code || "",
      (tx.amount ?? 0).toString(),
      (tx.total_fee ?? tx.fee_amount ?? 0).toString(),
      (tx.net_amount ?? tx.amount ?? 0).toString(),
      tx.status,
      tx.created_date || tx.createdAt
    ]);
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([[h.join(","), ...rows.map(r => r.join(","))].join("\n")], { type: "text/csv" })); a.download = `global-transactions-${new Date().toISOString().split("T")[0]}.csv`; a.click();
  }, [combinedData, merchants, resellers, t]);

  const selectedMerchant = selectedTx ? (selectedTx.merchant || (selectedTx.merchant_id ? merchants.find(m => m.id === selectedTx.merchant_id) : null)) : null;
  const selectedReseller = selectedTx ? (selectedTx.reseller || (selectedMerchant?.createdByResellerId ? resellers.find(r => r.id === selectedMerchant.createdByResellerId) : null)) : null;
  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => v && v !== "all" && k !== "search").length;
  const totalDeposits = combinedData.filter(t => t.type === "DEPOSIT" || t.type === "deposit" || t.type === "Deposit").length;
  const totalWithdrawals = combinedData.filter(t => t.type === "WITHDRAWAL" || t.type === "withdrawal" || t.type === "Withdrawal").length;

  return (
    <div className="gtx" style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Outfit',system-ui,sans-serif" }}>
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(34,211,238,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.02) 1px,transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 30%,black 20%,transparent 100%)" }} />
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 1000px 600px at 5% 5%,rgba(34,211,238,0.04) 0%,transparent 70%),radial-gradient(ellipse 800px 500px at 95% 85%,rgba(59,130,246,0.04) 0%,transparent 65%)" }} />

      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

        {error && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 18, borderRadius: 16, background: "rgba(251,113,133,0.09)", border: "1px solid rgba(251,113,133,0.22)" }}>
            <AlertCircle size={18} style={{ color: C.rose, flexShrink: 0, marginTop: 1 }} />
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: C.rose, margin: 0 }}>{t("errorLoadingTransactions")}</p>
              <p style={{ fontSize: 12, color: "rgba(251,113,133,0.65)", margin: "2px 0 0" }}>{error.message || t("pleaseCheckConnection")}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ display: "flex", flexDirection: windowWidth < 768 ? "column" : "row", justifyContent: "space-between", alignItems: windowWidth < 768 ? "stretch" : "flex-start", gap: windowWidth < 768 ? 16 : 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: windowWidth < 640 ? 12 : 16 }}>
            <div style={{ width: windowWidth < 640 ? 48 : 60, height: windowWidth < 640 ? 48 : 60, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "linear-gradient(135deg,rgba(34,211,238,0.1) 0%,rgba(59,130,246,0.1) 100%)", border: "1px solid rgba(34,211,238,0.22)", boxShadow: "0 8px 24px rgba(34,211,238,0.1)" }}>
              <Globe size={windowWidth < 640 ? 20 : 26} style={{ color: C.cyan }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: windowWidth < 640 ? 4 : 6, flexWrap: "wrap" }}><LiveDot /><span style={{ fontSize: windowWidth < 640 ? 9 : 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: C.t3 }}>Real-time · Auto-refresh 5s</span></div>
              <h1 style={{ fontSize: windowWidth < 640 ? 22 : windowWidth < 768 ? 26 : 30, fontWeight: 800, color: C.t1, margin: 0, letterSpacing: "-0.6px" }}>{t("globalTransactions")}</h1>
              <p style={{ fontSize: windowWidth < 640 ? 12 : 13, color: C.t3, margin: windowWidth < 640 ? "4px 0 0" : "6px 0 0", fontWeight: 500 }}>
                {isLoading ? t("loading") : `${totalTransactions.toLocaleString()} ${t("transactionsAcrossEntities")}`}
                {!isLoading && (totalTransactions > 0) && <span style={{ color: C.t4 }}> · {totalTransactions.toLocaleString()} {t("total")}</span>}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: windowWidth < 768 ? "row" : "column", alignItems: windowWidth < 768 ? "center" : "flex-end", gap: windowWidth < 768 ? 12 : 14, flexWrap: windowWidth < 768 ? "wrap" : "nowrap", justifyContent: windowWidth < 768 ? "space-between" : "flex-end" }}>
            {!isLoading && <div style={{ display: "flex", gap: windowWidth < 640 ? 8 : 10, flexWrap: "wrap", justifyContent: windowWidth < 768 ? "center" : "flex-end" }}><StatChip icon={ArrowDownToLine} label="Deposits" value={totalDeposits.toLocaleString()} color={C.cyan} /><StatChip icon={ArrowUpFromLine} label="Withdrawals" value={totalWithdrawals.toLocaleString()} color={C.emerald} /></div>}
            <div style={{ display: "flex", gap: windowWidth < 640 ? 8 : 10, flexWrap: "wrap", justifyContent: windowWidth < 768 ? "space-between" : "flex-end", width: windowWidth < 768 ? "100%" : "auto" }}><IconBtn onClick={handleRefresh} disabled={isRefreshing} spinning={isRefreshing} icon={RefreshCw} label={t("refresh")} variant="cyan" /><IconBtn onClick={handleExport} icon={Download} label={t("export")} /></div>
          </div>
        </div>

        {/* Filters */}
        <Panel noPad>
          <button onClick={() => setFiltersOpen(v => !v)}
            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", background: "transparent", border: "none", cursor: "pointer", borderRadius: "16px 16px 0 0", transition: "background .2s" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.03)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", background: C.cyanSoft, border: "1px solid rgba(34,211,238,0.18)" }}><SlidersHorizontal size={16} style={{ color: C.cyan }} /></div>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>{t("filters")}</span>
              {activeFiltersCount > 0 && <span style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 22, height: 22, padding: "0 6px", borderRadius: "50%", fontSize: 11, fontWeight: 700, background: C.cyan, color: C.bg, boxShadow: `0 0 12px ${C.cyan}40` }}>{activeFiltersCount}</span>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {filters.status !== "all" && <FilterPill label={filters.status} onRemove={() => setFilter("status", "all")} />}
                {filters.type !== "all" && <FilterPill label={filters.type} onRemove={() => setFilter("type", "all")} />}
                {filters.dateFrom && <FilterPill label={`From ${format(filters.dateFrom, "MMM d")}`} onRemove={() => setFilter("dateFrom", null)} />}
                {filters.dateTo && <FilterPill label={`To ${format(filters.dateTo, "MMM d")}`} onRemove={() => setFilter("dateTo", null)} />}
              </div>
              {activeFiltersCount > 0 && <button onClick={e => { e.stopPropagation(); resetFilters(); }} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: C.t3, background: "none", border: "none", cursor: "pointer", padding: "6px 10px", borderRadius: 8, transition: "all .2s" }} onMouseEnter={e => { e.currentTarget.style.color = C.rose; e.currentTarget.style.background = "rgba(251,113,133,0.08)"; }} onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.background = "none"; }}><X size={13} strokeWidth={2.5} /> Clear all</button>}
              <span style={{ display: "inline-flex", color: C.t3, transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .3s cubic-bezier(0.4,0,0.2,1)" }}><ChevronRight size={18} style={{ transform: "rotate(90deg)" }} /></span>
            </div>
          </button>

          <div style={{ overflow: "hidden", maxHeight: filtersOpen ? "600px" : "0", opacity: filtersOpen ? 1 : 0, transition: "max-height .35s cubic-bezier(0.4,0,0.2,1),opacity .25s ease" }}>
            <div style={{ padding: "0 24px 24px", borderTop: `1px solid ${C.border}` }}>
              <div style={{ paddingTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14 }}>
                <SearchInput value={filters.search} onChange={e => setFilter("search", e.target.value)} placeholder={t("searchIdRefMerchant")} />
                <DarkSelect value={filters.engine} onValueChange={v => handleEntityFilterChange("engine", v)} placeholder={t("allEngines")}><SelectItem value="all">{t("allEngines")}</SelectItem>{engines.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</DarkSelect>
                <DarkSelect value={filters.reseller} onValueChange={v => handleEntityFilterChange("reseller", v)} placeholder={t("allResellers")}><SelectItem value="all">{t("allResellers")}</SelectItem>{resellers.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</DarkSelect>
                <DarkSelect value={filters.merchant} onValueChange={v => handleEntityFilterChange("merchant", v)} placeholder={t("allMerchants")}><SelectItem value="all">{t("allMerchants")}</SelectItem>{merchants.map(m => <SelectItem key={m.id} value={m.id}>{getMerchantName(m, language, t)} ({m.code})</SelectItem>)}</DarkSelect>
                <DarkSelect value={filters.type} onValueChange={v => setFilter("type", v)} placeholder={t("allTypes") || "All types"}><SelectItem value="all">{t("allTypes") || "All types"}</SelectItem><SelectItem value="deposit">{t("deposit")}</SelectItem><SelectItem value="withdrawal">{t("withdrawal")}</SelectItem></DarkSelect>
                <DarkSelect value={filters.provider} onValueChange={v => setFilter("provider", v)} placeholder={t("allProviders")}><SelectItem value="all">{t("allProviders")}</SelectItem>{providerOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</DarkSelect>
                <DarkSelect value={filters.status} onValueChange={v => setFilter("status", v)} placeholder={t("allStatus")}><SelectItem value="all">{t("allStatus")}</SelectItem><SelectItem value="pending">{t("pending")}</SelectItem><SelectItem value="declined">{t("declined")}</SelectItem><SelectItem value="success">{t("success")}</SelectItem></DarkSelect>
                <DatePickerBtn selected={filters.dateFrom} onSelect={d => setFilter("dateFrom", d)} placeholder={t("fromDate")} />
                <DatePickerBtn selected={filters.dateTo} onSelect={d => setFilter("dateTo", d)} placeholder={t("toDate")} />
              </div>
            </div>
          </div>
        </Panel>

        <Panel noPad className="overflow-hidden">
          <AdminTransactionTable transactions={paginatedTransactions} isLoading={isLoading} onView={setSelectedTx} showMerchant showReseller showEngine={false} merchants={merchants} resellers={resellers} engines={engines} isQRCode={false} mixedTypes />
        </Panel>

        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexDirection: windowWidth < 640 ? "column" : "row", gap: windowWidth < 640 ? 16 : 0 }}>
            <p style={{ fontSize: windowWidth < 640 ? 12 : 13, fontWeight: 600, color: C.t3, margin: 0, order: windowWidth < 640 ? 2 : 0 }}>
              <span style={{ color: C.t1 }}>{(page * pageSize + 1).toLocaleString()}–{Math.min((page + 1) * pageSize, totalTransactions).toLocaleString()}</span> of <span style={{ color: C.cyan }}>{totalTransactions.toLocaleString()}</span> {t("transactions")}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: windowWidth < 640 ? 6 : 10, order: windowWidth < 640 ? 1 : 0 }}>
              <PageBtn onClick={() => setPage(page - 1)} disabled={page === 0}><ChevronLeft size={windowWidth < 640 ? 14 : 16} /></PageBtn>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center" }}>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => { const p = Math.max(0, Math.min(page - 2, totalPages - 5)) + i; const active = p === page; const isMobile = windowWidth < 640; return <button key={p} onClick={() => setPage(p)} style={{ width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 9, fontSize: isMobile ? 12 : 13, fontWeight: 700, cursor: "pointer", background: active ? C.cyan : C.card, color: active ? C.bg : C.t3, border: `1px solid ${active ? C.cyan : C.border}`, boxShadow: active ? "0 0 18px rgba(34,211,238,0.35)" : "none", transition: "all .2s ease", transform: active ? "scale(1.05)" : "scale(1)" }}>{p + 1}</button>; })}
              </div>
              <PageBtn onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><ChevronRight size={windowWidth < 640 ? 14 : 16} /></PageBtn>
            </div>
          </div>
        )}
      </div>

      <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} engine={null} reseller={selectedReseller} merchant={selectedMerchant} isQRCode={selectedTx?.type !== "WITHDRAWAL"} />
    </div>
  );
}